import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import type React from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { ButtonUI } from "../../../../components/Button";
import { InputUI } from "../../../../components/rhfs/Input";
import type { SignUpInput } from "../../../../hooks/authProvider/authProvider";

type SignUpInputUIProps = {
  onSignUp: SubmitHandler<SignUpInput>;
  defaultEmail?: string;
};

export const SignUpInputUI: React.VFC<SignUpInputUIProps> = ({
  onSignUp,
  defaultEmail,
}) => {
  const schema = Joi.object({
    familyName: Joi.string().required().messages({
      "string.empty": "姓は必須項目です",
    }),
    givenName: Joi.string().required().messages({
      "string.empty": "名は必須項目です",
    }),
    familyNameKana: Joi.string().allow(""),
    givenNameKana: Joi.string().allow(""),
    email: Joi.string().required().messages({
      "string.empty": "メールアドレスは必須項目です",
    }),
    password: Joi.string().required().messages({
      "string.empty": "パスワードは必須項目です",
    }),
  });
  const methods = useForm<SignUpInput>({
    resolver: joiResolver(schema),
    defaultValues: {
      email: defaultEmail || "",
    },
  });

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSignUp)} className="space-y-4">
            {/* 氏名 */}
            <div>
              <label className="label">
                <span className="label-text font-medium">氏名</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <div className="flex gap-3">
                <InputUI
                  registerName="familyName"
                  placeholder="姓"
                  data-testid="sign-up-input-family-name"
                />
                <InputUI
                  registerName="givenName"
                  placeholder="名"
                  data-testid="sign-up-input-given-name"
                />
              </div>
            </div>

            {/* ふりがな */}
            <div>
              <label className="label">
                <span className="label-text font-medium">ふりがな</span>
                <span className="label-text-alt text-base-content/50">
                  任意
                </span>
              </label>
              <div className="flex gap-3">
                <InputUI
                  registerName="familyNameKana"
                  placeholder="せい"
                  data-testid="sign-up-input-family-name-kana"
                />
                <InputUI
                  registerName="givenNameKana"
                  placeholder="めい"
                  data-testid="sign-up-input-given-name-kana"
                />
              </div>
            </div>

            {/* メールアドレス */}
            <div>
              <label className="label">
                <span className="label-text font-medium">メールアドレス</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <InputUI
                registerName="email"
                type="email"
                placeholder="example@company.com"
                data-testid="sign-up-input-username"
              />
            </div>

            {/* パスワード */}
            <div>
              <label className="label">
                <span className="label-text font-medium">パスワード</span>
                <span className="label-text-alt text-error">必須</span>
              </label>
              <InputUI
                registerName="password"
                type="password"
                placeholder="パスワードを入力"
                data-testid="sign-up-input-password"
              />
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  大文字、小文字、数字、記号を含む8文字以上
                </span>
              </label>
            </div>

            {/* 登録ボタン */}
            <div className="pt-4">
              <ButtonUI
                type="submit"
                color="primary"
                className="w-full"
                data-testid="sign-up-input-signup"
              >
                アカウントを作成
              </ButtonUI>
            </div>
          </form>
        </FormProvider>

        {/* ログインリンク */}
        <div className="divider text-base-content/50 text-sm">または</div>
        <p className="text-center text-sm">
          すでにアカウントをお持ちですか？{" "}
          <Link
            to="/login"
            data-testid="sign-up-input-to-signin"
            className="link link-primary font-medium"
          >
            ログイン
          </Link>
        </p>
      </div>
    </div>
  );
};
